import React from "react";
import { config } from "../config/config";

let CONFIG = null;

const loggers = new Map();
const levels = ["log", "info", "warn", "error"];
const cache = [];

const init = async conf => {
  CONFIG = conf.debug;

  if (window && CONFIG.enabled) {
    window.loggers = loggers;
    window.getLogger = getLogger;
    window.appendToLoggers = appendToLoggers;
    window.filterLoggers = filterLoggers;
    window.filterLevel = filterLevel;
    window.showLogger = showLogger;
    window.hideLogger = hideLogger;
    window.clearLogger = clearLogger;
    window.getLogData = getLogData;

    window.onerror = e => appendToLoggers(e, "error");
  }
};

config.get().then(init);

class Logger {
  constructor(name = "DefaultLogger", level = 0, enabled = true, appender = Logger.defaultAppender) {
    this.name = name;
    this.level = levels[level];
    this.enabled = enabled;
    this._appender = new Promise(resolve => {
      resolve(appender);
    });
  }

  static defaultAppender = window.console;

  set appender(appender) {
    this._appender = new Promise(resolve => {
      resolve(appender);
    });
  }

  get appender() {
    return this._appender;
  }

  /*set level(level) {
    this.appender && this.appender.forceUpdate && this.appender.forceUpdate();
  }*/

  _log(message, scope, level) {
    cache.push({ message, scope, level });
    this.enabled &&
      levels.indexOf(this.level) <= level &&
      this.appender &&
      this.appender.then(appender => {
        appender.forceUpdate && appender.forceUpdate();
        appender[levels[level]] &&
          appender[levels[level]].call(null, `[${this.name}${scope ? `:${scope}` : ""}]`, message);
      });
  }

  log(scope, message) {
    this._log(scope, message, 0);
  }

  info(scope, message) {
    this._log(scope, message, 1);
  }

  warn(scope, message) {
    this._log(scope, message, 2);
  }

  error(scope, message) {
    this._log(scope, message, 3);
  }

  show() {
    this.appender && this.appender.then(appender => appender.show && appender.show());
  }

  hide() {
    this.appender && this.appender.then(appender => appender.hide && appender.hide());
  }

  clear() {
    this.appender && this.appender.then(appender => appender.clear && appender.clear());
  }
}

export const getLogger = (loggerName = "App", conf) => {
  if (!loggers.has(loggerName)) {
    createLogger(loggerName, conf);
  } else if (conf) {
    const logger = loggers.get(loggerName);
    const editables = ["level", "enabled", "appender"];
    for (let attr in conf) {
      editables.includes(attr) && (logger[attr] = conf[attr]);
    }
  }
  return loggers.get(loggerName);
};

export const appendToLoggers = (message, level = "log") => {
  loggers.forEach(logger => logger[level](message));
};

export const createLogger = (logger, conf = {}) => {
  loggers.set(logger, new Logger(logger, conf.level, conf.enabled, conf.appender));
};

export const destroyLogger = logger => {
  loggers.has(logger) && loggers.delete(logger);
};

export const filterLoggers = (names = []) => {
  loggers.forEach(logger => (logger.enabled = names.includes(logger.name)));
};

export const filterLevel = (level = "log") => {
  loggers.forEach(logger => (logger.level = level));
};

export const showLogger = () => {
  loggers.forEach(logger => logger.show());
};

export const hideLogger = () => {
  loggers.forEach(logger => logger.hide());
};

export const clearLogger = () => {
  loggers.forEach(logger => logger.clear());
};

export const getLogData = () => cache;

export const Log = {
  getLogger,
  appendToLoggers,
  filterLoggers,
  filterLevel,
  showLogger,
  hideLogger,
  clearLogger,
  getLogData
};

export class LoggerOverlay extends React.Component {
  constructor(props) {
    super(props);
    this.styleLogger = {
      position: "fixed",
      top: "0",
      left: "0",
      border: "1px solid yellow",
      color: "#fff",
      padding: "10px",
      width: "100%",
      zIndex: "999",
      boxSizing: "border-box",
      margin: "0",
      pointerEvents: "none",
      overflow: "auto",
      fontFamily: "monospace",
      direction: "ltr"
    };
    this.styleMessage = {
      fontSize: "1.5em",
      lineHeight: "1.5em",
      whiteSpace: "pre-wrap"
    };
    this.styleScope = {
      fontWeight: "bold",
      marginRight: "20px"
    };
    this.styleOverlay = {
      position: "absolute",
      backgroundColor: "#000",
      opacity: CONFIG.overlay.opacity,
      top: "0",
      left: "0",
      boxSizing: "border-box",
      pointerEvents: "none",
      zIndex: "-1",
      height: "100%",
      width: "100%"
    };
    this.styleBar = {
      zIndex: "999",
      textAlign: "right",
      position: "absolute",
      right: "10px"
    };
    this.styleMemory = value => ({
      fontSize: "1.2rem",
      padding: "0 10px",
      color: value < 10 ? "#e91e63" : value < 20 ? "#ff9800" : "green"
    });
    this.styleButtons = {
      pointerEvents: "auto",
      border: "3px solid #fff",
      borderRadius: "7px",
      backgroundColor: "transparent",
      color: "#fff",
      padding: "5px 10px",
      marginLeft: "10px",
      fontSize: "1.5em"
    };
    this.maxLines = props.maxLines || CONFIG.lines;
    this.indent = props.indent || 0;
    this.state = {
      messages: [],
      levels,
      availableMemory: 100,
      show: true
    };
    this.levelColors = {
      log: "#fff",
      info: "#2196f3",
      warn: "#ff9800",
      error: "#e91e63"
    };
  }

  show = () => this.setState({ show: true });

  hide = () => this.setState({ show: false });

  toggle = () => this.setState(({ show }) => ({ show: !show }));

  log = (scope, message) => this.append(scope, "log", message);

  info = (scope, message) => this.append(scope, "info", message);

  warn = (scope, message) => this.append(scope, "warn", message);

  error = (scope, message) => this.append(scope, "error", message);

  clear = () => this.setState({ messages: [] });

  getTime = (date = false, time = new Date()) =>
    `${date ? time.toLocaleDateString() : ""} ${time.toLocaleTimeString()}`.trim();

  append = (scope, level = "log", message = this.getTime()) => {
    this.setState(({ messages, levels }) => {
      const newState = { messages, levels };
      newState.messages.unshift({
        message: JSON.stringify(message, null, this.indent),
        level,
        scope
      });
      newState.messages.length === this.maxLines && newState.messages.pop();
      return newState;
    });
  };

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  componentDidMount() {
    this.timer = setInterval(() => {
      window.tizen &&
        this.setState({
          availableMemory: Math.round(
            (window.tizen.systeminfo.getAvailableMemory() / window.tizen.systeminfo.getTotalMemory()) * 100
          )
        });
    }, 200);
    this.info(
      `
Logger started (⌐■_■)`,
      `${this.getTime(true)}`
    );
    this.append("scope", null, "message");
    const cachedData = getLogData();
    cachedData.forEach(({ message, scope, level }) => {
      this.append(`[CACHED:${scope}]`, levels[level], message);
    });
    this.props.onRef && this.props.onRef(this);
  }

  render() {
    const { height = CONFIG.overlay.height } = this.props;
    return (
      <div style={{ ...this.styleLogger, height }}>
        <div style={this.styleBar}>
          {window.tizen &&
            `${Math.round(window.tizen.systeminfo.getAvailableMemory() / Math.pow(1024, 2))} MB / ${Math.round(
              window.tizen.systeminfo.getTotalMemory() / Math.pow(1024, 2)
            )} MB`}
          <span style={this.styleMemory(this.state.availableMemory)}>{this.state.availableMemory}%</span>
          {this.state.show && (
            <button style={this.styleButtons} onClick={this.clear}>
              CLEAR
            </button>
          )}
          <button style={this.styleButtons} onClick={this.toggle}>
            {this.state.show ? "HIDE" : "SHOW"}
          </button>
        </div>
        {this.state.show && <div style={this.styleOverlay} />}
        {this.state.show && (
          <div style={{ paddingTop: "50px" }}>
            {this.state.messages.map(
              (item, index) =>
                this.state.levels.indexOf(item.level) !== -1 && (
                  <div
                    style={{
                      ...this.styleMessage,
                      color: this.levelColors[item.level]
                    }}
                    key={index}
                  >
                    <span style={this.styleScope}>{item.scope}</span>
                    {item.message}
                  </div>
                )
            )}
          </div>
        )}
      </div>
    );
  }
}
